import React, { useState } from "react";
import {
    Box,
    Grid,
    FormHelperText,
    makeStyles,
    useTheme,
    useMediaQuery,
    Typography,
    Link,
} from '@material-ui/core';

import { useCalculator } from '../../state/calculatorState';
import { TaxChartSlider, SliderThumbComponent, StyledTextField, PrimaryStaticTextField, StaticTextField } from '../../components/common/CustomComponents'


import { PriceIntegerFormat, PercentFormat } from '../common/customFormatters';
import { taxData } from '../../core/calculatorData';
import { getBracket, increaseBrackets, calculatePAYG, calculateBracket } from "../../core/Calculator";
import { isUndefined } from "../../core/calculatorUtils";

import {
    ZERO_VALUE,
    MONTH_NAMES,
    STUDENT_LOAN_INDEXATION,
    STUDENT_LOAN_INDEXATION_VALUE,
    STUDENT_LOAN_TYPICAL,
    WAGE_INDEXATION,
} from '../../constants';
import {MONTHLY} from '../../core/calculatorConstants'

import { formatMoney, getFinancialYear, parseInputValue } from "../../utils/utils";

const useStyles = makeStyles(theme => {
    return {
        image: {
            width: '100%',
            paddingBottom: 20,
        },
        helperText: {
            color: theme.palette.primary.contrastText,
            lineHeight:"1.2",
            paddingTop:2
        },
        switchHelperText: {
            opacity: 0.5,
            marginTop: -15,
            paddingLeft: 0,
            marginLeft: 45,
            color: theme.palette.primary.contrastText,
            letterSpacing: "-0.15px",
            lineHeight: "1.25",
        },

    }
});


export const RepaymentCalculator = React.memo(() => {
    const theme = useTheme();
    const classes = useStyles();

    const small = useMediaQuery(theme.breakpoints.down('sm'));

    //let [{ income: salary },] = useCalculator();
    const [{ adjustedAnnualTaxableIncome }] = useCalculator();

    const currentYearIndex = taxData.findIndex(t => t.current === "true");
    const helpData = taxData[currentYearIndex].help;

    //const [income, setIncome] = useState(salary.a);
    const [debt, setDebt] = useState(STUDENT_LOAN_TYPICAL);
    const [repaymentIncome, setRepaymentIncome] = useState(adjustedAnnualTaxableIncome);


    const [debtIndexation, setDebtIndexation] = useState(STUDENT_LOAN_INDEXATION_VALUE);
    const [wageIndexation, setWageIndexation] = useState(WAGE_INDEXATION);


    const monthlyIncome = repaymentIncome / 12;

    let repaymentBrackets = [...helpData.brackets];
    let repaymentBracket = getBracket(repaymentIncome, repaymentBrackets);
    let repaymentRate = repaymentBracket.value;

    const [masterSalary, setMasterSalary] = useState(adjustedAnnualTaxableIncome);

    if (masterSalary !== adjustedAnnualTaxableIncome) {
        setMasterSalary(adjustedAnnualTaxableIncome);
        setRepaymentIncome(adjustedAnnualTaxableIncome);
    }

    let date = new Date();
    let debtRemaining = debt;
    let financialYear = getFinancialYear(date);
    let monthlyRepayment = monthlyIncome * (repaymentRate / 100);

    let startMonthlyRepayment =  0

    if( isUndefined(helpData.payg)){
        startMonthlyRepayment = calculateBracket(repaymentIncome,repaymentBrackets, helpData.incremental) / 12
    }else{
        // use payg
        startMonthlyRepayment = calculatePAYG(repaymentIncome/12, helpData.payg, MONTHLY);
    }

    let repaymentCount = 0
    let indexedRepaymentInome = repaymentIncome;
    let indexedMonthlyIncome = monthlyIncome;

    while (debtRemaining > 0 && repaymentRate > 0) {

        // add one month
        date.setMonth(date.getMonth() + 1);

        const newFinancialYear = getFinancialYear(date);
        if (newFinancialYear !== financialYear) {
            financialYear = newFinancialYear;
            repaymentBrackets = increaseBrackets(repaymentBrackets, 1 + debtIndexation);

            indexedRepaymentInome *= 1 + wageIndexation;
            indexedMonthlyIncome *= 1 + wageIndexation;

            repaymentBracket = getBracket(indexedRepaymentInome, repaymentBrackets);
            repaymentRate = repaymentBracket.value;
            monthlyRepayment = indexedMonthlyIncome * (repaymentRate / 100);
            // increase debt by 2%
            debtRemaining *= 1 + debtIndexation;
        };

        debtRemaining -= monthlyRepayment;
        repaymentCount++;
    }

    let remaymentDateDescription = MONTH_NAMES[date.getMonth()] + " " + date.getFullYear();
    const years = Math.floor(repaymentCount / 12);
    const yearsPlusMonths = repaymentCount % 12;

    let remaymentDescription = years > 0 ? `${years} year${years > 1 ? 's' : ''}` : '';
    if (yearsPlusMonths > 0) {
        if (years > 0) {
            remaymentDescription += ` and `;
        }
        remaymentDescription += `${yearsPlusMonths} month${yearsPlusMonths > 1 ? 's' : ''}`;
    }

    if (monthlyRepayment === 0) {
        if (debt === debtRemaining) {
            /// monthsremaymentDescription = "Indefinite";
            remaymentDescription = `Income is below minimum repayment threshold`;
            remaymentDateDescription = `Your debt will remain at $${formatMoney(debtRemaining, 0)}`
        } else {
            remaymentDescription = `In ${remaymentDateDescription} your current income will fall below the minimum threshold`
            remaymentDateDescription = `In ${remaymentDateDescription} your debt will be $${formatMoney(debtRemaining, 0)}`
        }

    }

    const MAX_INCOME = Number(masterSalary * 2).toPrecision(2);
    const MAX_DEBT = STUDENT_LOAN_TYPICAL*3;

    const handleChangeIncome = () => event => {
        if (document.activeElement.className !== event.target.className) return;
        let newValue = parseInputValue(event.target.value);
        newValue = newValue.toLocaleString('fullwide', { useGrouping: false })
        if (debt === ZERO_VALUE) {
            newValue = (newValue - ZERO_VALUE) / 10;
        }
        const value = newValue > 0 ? newValue : ZERO_VALUE;
        setRepaymentIncome(value);
    }


    const modifyIncome = (event, value) => {
        setRepaymentIncome(value);
    }

    const handleChangeDebt = () => event => {
        if (document.activeElement.className !== event.target.className) return;
        let newValue = parseInputValue(event.target.value);
        newValue = newValue.toLocaleString('fullwide', { useGrouping: false })
        if (debt === ZERO_VALUE) {
            newValue = (newValue - ZERO_VALUE) / 10;
        }
        const value = newValue > 0 ? newValue : ZERO_VALUE;
        setDebt(value);
    }
    const modifyDebt = (event, value) => {
        setDebt(value);
    }


    return (
        <Box style={{ padding: '0px 20px' }}>
            <Grid container spacing={4} alignItems="flex-start">

                <Grid item xs={6}>
                    <StyledTextField
                        label={"Student loan debt"}
                        fullWidth
                        value={debt > 0 ? debt : null}
                        defaultValue={0}
                        variant="filled"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: PriceIntegerFormat,
                            inputMode: 'numeric',
                            inputProps: {
                                inputMode: 'numeric',
                            },
                        }}
                        onChange={handleChangeDebt()}
                        id="debt"
                    />
                    <Typography variant="body2" style={{ paddingTop: 10, marginBottom: -1 }}>Adjust debt</Typography>
                    <TaxChartSlider
                        ThumbComponent={SliderThumbComponent}
                        valueLabelDisplay="off"
                        min={0}
                        max={Number(MAX_DEBT)}
                        step={100}
                        aria-label="Adjust debt"
                        value={debt}
                        onChange={modifyDebt}
                    />
                </Grid>

                <Grid item xs={6}>
                    <StyledTextField
                        label={"Repayment Income"}
                        fullWidth
                        //value={`$${formatMoney(repaymentIncome, 0)}`}
                        value={repaymentIncome > 0 ? repaymentIncome : null}
                        defaultValue={0}
                        variant="filled"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: PriceIntegerFormat,
                            inputMode: 'numeric',
                            inputProps: {
                                inputMode: 'numeric',
                            },
                        }}
                        onChange={handleChangeIncome()}
                        id="repaymentIncome_studentloan"
                    />
                    <Typography variant="body2" style={{ paddingTop: 10, marginBottom: -1 }}>Adjust Repayment Income</Typography>
                    <TaxChartSlider
                        ThumbComponent={SliderThumbComponent}
                        valueLabelDisplay="off"
                        min={0}
                        max={Number(MAX_INCOME)}
                        step={1000}
                        aria-label="Adjust income"
                        value={Number(repaymentIncome)}
                        onChange={modifyIncome}
                    />
                </Grid>

                <Grid item xs={6} style={{paddingTop:0}} >
                    <PrimaryStaticTextField
                        label={"Monthly Repayment"}
                        fullWidth
                        value={`$${formatMoney(startMonthlyRepayment, 0)}`}
                        variant="filled"
                        type="text"
                        id="startMonthlyRepayment"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4} alignItems="flex-start">
                <Grid item xs={12} style={{paddingTop: 30, paddingBottom:0, marginBottom:-5}}>
                <Typography variant="h3" style={{ fontSize: 16, color: theme.palette.primary.main }}>{`Student Loan Repayment`}</Typography>
                <Typography variant="body1" >Calculate the final repayment date of your debt. Adjust the average debt indexation rate to improve the projection as the Tax Office <Link href={STUDENT_LOAN_INDEXATION} rel="noopener noreferrer" target="_blank" title="Studen loan indexation">increases the debt</Link> and bracket thresholds inline with inflation. An increase in your wage over the life of the debt will pay off your loan sooner. </Typography>
                
                </Grid>

                <Grid item md={3} xs={6} >
                    <StyledTextField
                        label="Debt Indexation"
                        fullWidth
                        value={debtIndexation*100}
                        defaultValue={0}
                        // placeholder={placeholder}
                        variant="filled"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: PercentFormat,
                            inputProps: {
                                inputMode: "decimal",
                                pattern: "[0-9].",
                            }
                        }}

                        onChange={(event) => {
                            let value = event.target.value;
                            value = isNaN(value) ? 0 : Number(value);
                            setDebtIndexation(parseInputValue(value/100))
                        }
                        }
                        id="Debt indexation"
                    />
                </Grid>
                <Grid item md={3} xs={6}>
                    <StyledTextField
                        label="Wage Increase"
                        fullWidth
                        value={wageIndexation*100}
                        defaultValue={0}
                        // placeholder={placeholder}
                        variant="filled"
                        type="text"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: PercentFormat,
                            inputProps: {
                                inputMode: "decimal",
                                pattern: "[0-9].",
                            }
                        }}

                        onChange={(event) => {
                            let value = event.target.value;
                            value = isNaN(value) ? 0 : Number(value);
                            setWageIndexation(parseInputValue(value/100))
                        }
                        }
                        id="wageIncrease"
                    />
                    {small && (
                        <FormHelperText className={classes.helperText}>
                        {`Income of $${formatMoney(indexedRepaymentInome, 0)} in ${ financialYear}`}
                </FormHelperText>
                    )}
                </Grid>
                {!small && (

               
                <Grid item md={6} xs={6}>
                <StaticTextField
                        label="Future income"
                        fullWidth
                        value={`$${formatMoney(indexedRepaymentInome, 0)} `}
                        variant="filled"
                        type="text"
                        id="futureIncome"
                    />
                    <FormHelperText className={classes.helperText}>
                    {`Income in the year ${ financialYear}`}
            </FormHelperText>
                </Grid>
                 )}

                <Grid item xs={6} style={{paddingTop:0, marginTop:-10}} >
                    <PrimaryStaticTextField
                        label="Repayment Date"
                        fullWidth
                        value={remaymentDateDescription}
                        variant="filled"
                        type="text"
                        id="repaymentDate"
                    />
                </Grid>
                <Grid item xs={6}>
                    <Box display="flex" flexDirection="row" alignItems="center" style={{paddingTop:0, marginTop:-10}}>
                <Typography variant="h3" style={{ fontSize: 16, color: theme.palette.primary.main }}>{`${remaymentDescription}`}</Typography>
                </Box>
                </Grid>
            </Grid>


            <FormHelperText className={classes.helperText}>
                Repayment income (RI) is taxable income plus any total net investment loss (which includes net rental losses), total reportable fringe benefits amounts, reportable super contributions and exempt foreign employment income.
            </FormHelperText>
        </Box>
    );
}
)

export default RepaymentCalculator